import React, { createContext, useContext, useReducer, useCallback } from 'react';

const SwotContext = createContext();

const initialState = {
  strengths: [],
  weaknesses: [],
  opportunities: [],
  threats: [],
  title: 'SWOT Analysis',
  description: 'Strategic planning tool to identify internal and external factors',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const swotReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      return {
        ...state,
        [action.category]: [
          ...state[action.category],
          {
            id: Date.now() + Math.random(),
            text: action.text,
            priority: action.priority,
            createdAt: new Date().toISOString()
          }
        ],
        updatedAt: new Date().toISOString()
      };

    case 'UPDATE_ITEM':
      return {
        ...state,
        [action.category]: state[action.category].map(item =>
          item.id === action.id
            ? { ...item, text: action.text, priority: action.priority }
            : item
        ),
        updatedAt: new Date().toISOString()
      };

    case 'DELETE_ITEM':
      return {
        ...state,
        [action.category]: state[action.category].filter(item => item.id !== action.id),
        updatedAt: new Date().toISOString()
      };

    case 'MOVE_ITEM':
      const sourceItems = state[action.sourceCategory].filter(item => item.id !== action.itemId);
      const itemToMove = state[action.sourceCategory].find(item => item.id === action.itemId);
      
      if (!itemToMove) return state;

      return {
        ...state,
        [action.sourceCategory]: sourceItems,
        [action.targetCategory]: [
          ...state[action.targetCategory],
          {
            ...itemToMove,
            id: Date.now() + Math.random() // Generate new ID to avoid conflicts
          }
        ],
        updatedAt: new Date().toISOString()
      };

    case 'UPDATE_TITLE':
      return {
        ...state,
        title: action.title,
        updatedAt: new Date().toISOString()
      };

    case 'UPDATE_DESCRIPTION':
      return {
        ...state,
        description: action.description,
        updatedAt: new Date().toISOString()
      };

    case 'LOAD_DATA':
      return {
        ...action.data,
        updatedAt: new Date().toISOString()
      };

    case 'RESET':
      return {
        ...initialState,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

    default:
      return state;
  }
};

export const SwotProvider = ({ children }) => {
  const [state, dispatch] = useReducer(swotReducer, initialState);

  const addItem = useCallback((category, text, priority = 'medium') => {
    dispatch({ type: 'ADD_ITEM', category, text, priority });
  }, []);

  const updateItem = useCallback((category, id, text, priority) => {
    dispatch({ type: 'UPDATE_ITEM', category, id, text, priority });
  }, []);

  const deleteItem = useCallback((category, id) => {
    dispatch({ type: 'DELETE_ITEM', category, id });
  }, []);

  const moveItem = useCallback((itemId, sourceCategory, targetCategory) => {
    if (sourceCategory !== targetCategory) {
      dispatch({ 
        type: 'MOVE_ITEM', 
        itemId, 
        sourceCategory, 
        targetCategory 
      });
    }
  }, []);

  const updateTitle = useCallback((title) => {
    dispatch({ type: 'UPDATE_TITLE', title });
  }, []);

  const updateDescription = useCallback((description) => {
    dispatch({ type: 'UPDATE_DESCRIPTION', description });
  }, []);

  const loadData = useCallback((data) => {
    dispatch({ type: 'LOAD_DATA', data });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const value = {
    ...state,
    addItem,
    updateItem,
    deleteItem,
    moveItem,
    updateTitle,
    updateDescription,
    loadData,
    reset
  };

  return (
    <SwotContext.Provider value={value}>
      {children}
    </SwotContext.Provider>
  );
};

export const useSwot = () => {
  const context = useContext(SwotContext);
  if (!context) {
    throw new Error('useSwot must be used within a SwotProvider');
  }
  return context;
};